import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Target, ArrowRight } from 'lucide-react';

interface ProfileEnhancementProps {
  profile: any;
  experiences: any[];
  education: any[];
  projects?: any[];
}

export function ProfileEnhancement({ 
  profile, 
  experiences, 
  education,
  projects 
}: ProfileEnhancementProps) {
  // Calculate completion percentages
  const basicInfoScore = calculateBasicInfoCompletion(profile);
  const experienceScore = calculateExperienceCompletion(experiences);
  const educationScore = calculateEducationCompletion(education);
  const projectsScore = calculateProjectsCompletion(projects || []);
  
  // Calculate overall completion
  const overallCompletion = Math.round(
    (basicInfoScore + experienceScore + educationScore + projectsScore) / 4
  );

  if (overallCompletion >= 90) {
    return null; // Hide when profile is mostly complete
  }

  return (
    <div className="bg-primary/5 rounded-lg p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Target className="h-4 w-4 text-primary" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Complete Your Profile</h3>
            <span className="text-xs font-medium text-primary">{overallCompletion}%</span>
          </div>
          <Progress value={overallCompletion} className="w-32 h-1.5 mt-1" />
        </div>
      </div>
      <Button size="sm" asChild>
        <Link to="/onboarding">
          Complete Profile
          <ArrowRight className="ml-2 h-3 w-3" />
        </Link>
      </Button>
    </div>
  );
}

// Helper functions
function calculateBasicInfoCompletion(profile: any): number {
  if (!profile) return 0;
  
  const requiredFields = ['full_name', 'title', 'summary', 'email', 'location'];
  const completedFields = requiredFields.filter(field => profile[field] && profile[field].trim() !== '');
  
  return Math.round((completedFields.length / requiredFields.length) * 100);
}

function calculateExperienceCompletion(experiences: any[]): number {
  if (!experiences || experiences.length === 0) return 0;
  
  const totalScore = experiences.reduce((score, exp) => {
    let itemScore = 0;
    if (exp.position) itemScore += 25;
    if (exp.company_name) itemScore += 25;
    if (exp.description && exp.description.length > 100) itemScore += 25;
    if (exp.start_date) itemScore += 25;
    return score + itemScore;
  }, 0);
  
  return Math.round(totalScore / experiences.length);
}

function calculateEducationCompletion(education: any[]): number {
  if (!education || education.length === 0) return 0;
  
  const totalScore = education.reduce((score, edu) => {
    let itemScore = 0;
    if (edu.institution) itemScore += 25;
    if (edu.degree) itemScore += 25;
    if (edu.field_of_study) itemScore += 25;
    if (edu.start_date) itemScore += 25;
    return score + itemScore;
  }, 0);
  
  return Math.round(totalScore / education.length);
}

function calculateProjectsCompletion(projects: any[]): number {
  if (!projects || projects.length === 0) return 0;
  
  const totalScore = projects.reduce((score, project) => {
    let itemScore = 0;
    if (project.title) itemScore += 25;
    if (project.description) itemScore += 25;
    if (project.technologies?.length > 0) itemScore += 25;
    if (project.url || project.github_url) itemScore += 25;
    return score + itemScore;
  }, 0);
  
  return Math.round(totalScore / projects.length);
}